import { Color } from '@pixi/color';
import { Container, Graphics } from 'pixi.js-legacy';

import { themingInstance } from '@exchange/libs/utils/theming/src';

import PriceLine from './pixi-price-line';
import SizeLine from './pixi-size-line';
import TextValue from './pixi-text-value';
import { DataPointModel } from '../interfaces';

export default class PriceIndicator extends Container {
  public indicatorName: string;

  private wrapper = new Container();

  private priceText: TextValue;

  private priceLine: PriceLine;

  private sizeLine: SizeLine;

  private itemMask = new Graphics();

  private shadowRect = new Graphics();

  private shadowRectAlpha = 0.7;

  private sizeLineX?: number;

  private verticalPadding = 8; // px

  private leftPadding = 6; // px

  private cachedDiffPercent = 0;

  private sideHeight: number;

  private doubleSideHeight: number;

  private parentWidth: number;

  private isAsk: boolean;

  private isHoverable: boolean;

  private getFormattedNumber: (value: number, isMarketPrecision?: boolean) => string;

  private get hoverRectColor() {
    return new Color(themingInstance.getColor('--v-theme-elevation-0')).toNumber();
  }

  constructor({
    name,
    sideHeight,
    parentWidth,
    isAsk,
    isHoverable,
    fontName,
    getFormattedNumber,
  }: {
    name: string;
    sideHeight: number;
    parentWidth: number;
    isAsk: boolean;
    isHoverable: boolean;
    fontName: string;
    getFormattedNumber: (value: number, isMarketPrecision?: boolean) => string;
  }) {
    super();

    this.indicatorName = name;
    this.sideHeight = sideHeight;
    this.doubleSideHeight = sideHeight * 2;
    this.parentWidth = parentWidth;
    this.isAsk = isAsk;
    this.isHoverable = isHoverable;
    this.getFormattedNumber = getFormattedNumber;

    this.priceText = new TextValue({ fontName, text: '' });
    this.priceText.x = this.leftPadding;
    this.priceLine = new PriceLine({ lineX: this.leftPadding, lineY: 0 });

    const recX = 0;
    const recY = this.isAsk ? 0 : this.sideHeight;
    const recWidth = this.parentWidth;
    const recHight = this.sideHeight;

    this.wrapper.mask = this.itemMask;
    this.itemMask.clear().beginFill().drawRect(recX, recY, recWidth, recHight).endFill();

    this.sizeLine = new SizeLine({
      lineX: 0,
      lineY: 0,
      isAsk: this.isAsk,
      sideHeight: this.sideHeight,
      fontName,
    });

    if (this.isHoverable) {
      this.shadowRect.clear().beginFill(this.hoverRectColor, this.shadowRectAlpha).drawRect(recX, recY, recWidth, recHight).endFill();

      this.wrapper.addChild(this.priceLine, this.sizeLine);
      this.addChild(this.shadowRect, this.wrapper, this.priceText);
    } else {
      this.wrapper.addChild(this.priceLine);
      this.addChild(this.wrapper, this.priceText);
    }
  }

  public update(position: DataPointModel): void {
    if ((position.price === 1 && position.size === 1) || +position.price === 0) {
      this.alpha = 0;

      return;
    }

    this.alpha = 1;

    let wrapperY = position.coordinates.y;

    this.priceLine.alpha = 1;

    if (this.isHoverable) {
      this.setSizeLabelAlpha(1);
      this.priceText.y = wrapperY - this.priceText.textHeight / 2 - 2;
    } else {
      this.priceText.y = wrapperY - this.priceText.textHeight / 2 - 2;
    }

    // This handles the ASK side outer boundaries
    if (this.isAsk) {
      if (position.coordinates.y > this.verticalPadding) {
        this.sizeLineX = position.coordinates.x;
      } else {
        this.priceText.y = 0;

        if (position.coordinates.y < 0) {
          wrapperY = 0;
          this.priceLine.alpha = 0;

          return;
        }
      }
    }

    // This handles the BID side outer boundaries
    if (!this.isAsk) {
      if (position.coordinates.y < this.sideHeight * 2 - this.verticalPadding) {
        this.sizeLineX = position.coordinates.x;
      } else {
        this.priceText.y = this.sideHeight * 2 - 16;

        if (position.coordinates.y > this.sideHeight * 2) {
          wrapperY = this.sideHeight * 2;
          this.priceLine.alpha = 0;

          return;
        }
      }
    }

    // ASK side inner boundaries
    if (this.isAsk && position.coordinates.y > this.sideHeight - this.priceText.textHeight) {
      this.priceText.y = this.sideHeight - this.priceText.textHeight - this.verticalPadding;
      this.setSizeLabelAlpha(0);
    }

    // BID side inner boundaries
    if (!this.isAsk && position.coordinates.y < this.sideHeight + this.priceText.textHeight) {
      this.priceText.y = this.sideHeight + this.priceText.textHeight / 2;
      this.setSizeLabelAlpha(0);
    }

    const positionPriceNumber = Number(position.price);

    this.wrapper.y = this.isHoverable ? wrapperY : wrapperY - 2;
    this.priceLine.x = this.leftPadding + this.priceText.textWidth + 6;

    const text = positionPriceNumber >= 0 ? positionPriceNumber : 0;

    this.priceText.text = this.getFormattedNumber(text);

    this.itemMask
      .clear()
      .beginFill()
      .drawRect(0, this.isAsk ? 3 : this.sideHeight - 1.5, Number(this.sizeLineX) + 2, this.isAsk ? this.sideHeight : this.sideHeight + 4)
      .endFill();

    if (positionPriceNumber >= 0) {
      this.cachedDiffPercent = position.priceDiffPercent;
    }

    if (this.isHoverable) {
      this.sizeLine.update(Number(this.sizeLineX) - this.x, positionPriceNumber < 0 ? this.cachedDiffPercent : position.priceDiffPercent, wrapperY);

      const recY = this.isAsk ? 0 : wrapperY;
      const recHight = this.isAsk ? wrapperY : this.doubleSideHeight - wrapperY;

      this.shadowRect.clear().beginFill(this.hoverRectColor, this.shadowRectAlpha).drawRect(0, recY, this.parentWidth, recHight).endFill();
    }
  }

  private setSizeLabelAlpha(a: number): void {
    this.sizeLine.label.alpha = a;
  }
}
